import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { ErrorAlert, Toast, ToastContainer } from 'smartsuite-ui';
import { putDataPromise } from 'brock-fetch-utils';
import download from 'downloadjs';
import LandingPageHeader from './LandingPageHeader';
import LandingPagePanel from './LandingPagePanel';
import { getGridTemplateStyles } from '../viewDashboards/dashboardUtils';
import EmptyLandingPagePanel from './EmptyLandingPagePanel';
import {
    removeFromArray,
    replaceInArray,
} from '../viewDashboards/widgets/utils/dataFormatUtils';
import DashboardContext from '../shared/hooks/dashboardContext';
import { useLabelContext } from '../shared/hooks/labelContext';
import { panelLayouts, landingPageKey } from './landingPageConstants';
import useWindowSize from '../shared/hooks/windowSizeHook';
import {
    downloadToastDisplayTime,
    minWindowWidths,
} from '../shared/constants/constants';
import { minimizePanelConfigsForSave } from './landingPageSetupUtils';

function gridRowsFromPanels(panels) {
    return panels.reduce((result, panel) => {
        const { row, rowSpan } = panel.position;
        const maxRow = row + rowSpan - 1;

        return maxRow > result ? maxRow : result;
    }, 0);
}

/* eslint-disable max-statements, complexity */
export default function LandingPage(props) {
    const { configuration } = props;

    const { dashboardLabels, exportLabels } = useLabelContext();
    const { uriConstants } = useContext(DashboardContext);

    const [errorMessage, setErrorMessage] = useState(null);
    const [panels, setPanels] = useState(configuration.panels);
    const [downloadsInProgress, setDownloadsInProgress] = useState(0);

    const { width } = useWindowSize();

    const saveLandingPage = useCallback(
        (updatedPanels) => {
            putDataPromise(uriConstants.saveUserPreferences, {
                key: landingPageKey,
                value: JSON.stringify({
                    ...configuration,
                    panels: minimizePanelConfigsForSave(updatedPanels),
                }),
            }).then(
                () => setErrorMessage(null),
                (error) =>
                    setErrorMessage(
                        `${dashboardLabels.errorSavingLandingPage}\n${error}`
                    )
            );
        },
        [configuration, dashboardLabels, uriConstants]
    );

    useEffect(() => {
        document.title =
            dashboardLabels[configuration.title] || configuration.title;
        saveLandingPage(configuration.panels);
        setPanels(configuration.panels);
    }, [configuration, dashboardLabels, saveLandingPage]);

    function handleAdd(panel) {
        const updatedPanels = [...panels, panel];
        saveLandingPage(updatedPanels);
        setPanels(updatedPanels);
    }

    function handleEdit(panel) {
        const indexInArray = _.findIndex(
            panels,
            (x) => x.panelIndex === panel.panelIndex
        );

        const updatedPanels = replaceInArray(panel, panels, indexInArray);
        saveLandingPage(updatedPanels);
        setPanels(updatedPanels);
    }

    function handleDelete(panelIndex) {
        const indexInArray = _.findIndex(
            panels,
            (x) => x.panelIndex === panelIndex
        );

        const updatedPanels = removeFromArray(panels, indexInArray);
        saveLandingPage(updatedPanels);
        setPanels(updatedPanels);
    }

    function onWidgetDownload(downloadData, widgetTitle) {
        setDownloadsInProgress((prevState) => prevState + 1);

        const fileName = widgetTitle
            ? `${configuration.title}__${widgetTitle}.csv`
            : `${configuration.title}.csv`;

        download(downloadData, fileName, 'text/csv');

        setTimeout(() => {
            setDownloadsInProgress((prevState) => prevState - 1);
        }, downloadToastDisplayTime);
    }

    function getPanelClassName() {
        let panelClassName = 'grid-panel';

        if (width < minWindowWidths.tablet) {
            panelClassName += ' grid-panel--mobile';
        } else if (
            width >= minWindowWidths.tablet &&
            width < minWindowWidths.desktop
        ) {
            panelClassName += ' grid-panel--tablet';
        } else {
            panelClassName += ' grid-panel--desktop';
        }

        return panelClassName;
    }

    function renderPanelsAndWidgets() {
        const panelClassName = getPanelClassName();
        return panelLayouts.map((panelLayout, index) => {
            const panel = panels.find((x) => x.panelIndex === index);

            if (!panel) {
                return (
                    <EmptyLandingPagePanel
                        key={index} // eslint-disable-line react/no-array-index-key
                        dashboardWidth={width}
                        handleAdd={handleAdd}
                        panelClassName={panelClassName}
                        panelIndex={index}
                        panelLayout={panelLayout}
                    />
                );
            }

            return (
                <LandingPagePanel
                    {...panel}
                    key={panel.panelIndex}
                    dashboardWidth={width}
                    handleDelete={handleDelete}
                    handleEdit={handleEdit}
                    onDownload={onWidgetDownload}
                    openLinksInNewTab={configuration.openLinksInNewTab}
                    panelClassName={panelClassName}
                    panelLayout={panelLayout}
                    displayViewTitle={panel.displayViewTitle}
                />
            );
        });
    }

    function renderToast() {
        return (
            <ToastContainer>
                <Toast
                    notificationMessage={`${downloadsInProgress} ${exportLabels.downloadInProgress}`}
                    isMounted={downloadsInProgress > 0}
                    onDismiss={() => setDownloadsInProgress(0)}
                />
            </ToastContainer>
        );
    }

    function addDesktopModifierClass(baseClass) {
        if (width >= minWindowWidths.desktop) {
            return `${baseClass} ${baseClass}--desktop`;
        }

        return baseClass;
    }

    return (
        <div className="dashboard" style={configuration.containerStyles}>
            {errorMessage && <ErrorAlert>{errorMessage}</ErrorAlert>}
            <LandingPageHeader
                onlineHelpId={configuration.onlineHelpId ?? 0}
                title={configuration.title}
            />
            <div className={addDesktopModifierClass('dashboard-widgets')}>
                <div
                    className={addDesktopModifierClass('dashboard-widget-grid')}
                    style={getGridTemplateStyles(
                        gridRowsFromPanels(panelLayouts)
                    )}
                >
                    {renderPanelsAndWidgets()}
                </div>
            </div>
            {renderToast()}
        </div>
    );
}
/* eslint-enable max-statements, complexity */

LandingPage.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
};
